import API from "../Services/API";
import Model from "./Model";
import State from "./State";
import { toQueryString } from "../Services/helpers";
import { useSimpleWrapper } from "./SimpleWrapper";

const useJobs = (search, alwaysRefresh = false) => {
    const searchStr = toQueryString(search);
    return Model({
        stateKey: ['jobs', searchStr],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/jobs?include=customer&${searchStr}`),
        returnDataKey: 'jobs',
        saveAdditionalData: (jobs) => {
            jobs.forEach(job => {
                State.putIfNotExists(['job', job.id], job)
                if (job.customer) {
                    State.putIfNotExists(['customer', job.customer.id], job.customer)
                }
            })
        },
        registerAdditionalMethods: (opts) => {
            return {
                deleteJob: (job) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${job.customerId}/jobs/${job.id}`),
                        'Job was deleted',
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading jobs.',
    })
}

const useJob = (jobId, alwaysRefresh = false) => {
    return Model({
        stateKey: ['job', jobId],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/jobs/${jobId}?include=customer`),
        returnDataKey: 'job',
        defaultLoadingError: 'Sorry, there was an error loading the job.',
    })
}

const useJobSummary = (alwaysRefresh = false) => {
    return Model({
        stateKey: 'jobs-summary',
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/jobs/summary`),
        returnDataKey: 'summary',
        defaultLoadingError: 'Sorry, there was an error loading the job summary.',
    })
}

const useScheduleSuggestion = (week, alwaysRefresh = false) => {
    return Model({
        subscribeToUpdates: {
            topic: 'schedule-suggestions',
            handler: (event, opts) => opts.refresh(),
        },
        stateKey: ['schedule-suggestion', week ?? 'current'],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/jobs/suggestedSchedule${week ? `/${week}` : ''}`),
        returnDataKey: 'dates',
        saveAdditionalData: (dates) => {
            Object.values(dates).forEach(users => {
                Object.values(users).forEach(user => {
                    const rounds = [...user.scheduled.rounds, ...user.suggested.rounds];
                    const roundJobs = [];
                    rounds.forEach(round => {
                        State.putIfNotExists(['round', round.id], round)
                        roundJobs.push(...round.jobs)
                    })

                    const jobs = [...roundJobs, ...user.scheduled.jobs, ...user.suggested.jobs];
                    jobs.forEach(job => {
                        State.putIfNotExists(['job', job.id], job)
                        if (job.customer) {
                            State.putIfNotExists(['customer', job.customer.id], job.customer)
                        }
                    });
                })
            })
        },
        defaultLoadingError: 'Sorry, there was an error loading the schedule suggestion.',
    })
}

const useWorkDate = (date, assignedTo, alwaysRefresh = false) => {
    return Model({
        assumeLoading: true,
        stateKey: ['jobs-work', date, assignedTo],
        alwaysRefresh: alwaysRefresh,
        persist: true,
        fetchData: () => API.get(`/jobs?scheduledOrDoneDate=${date}&assignedTo=${assignedTo ?? ''}&include=customer`),
        saveAdditionalData: (jobs) => {
            jobs.forEach(job => {
                State.putIfNotExists(['job', job.id], job)
            })
        },
        returnDataKey: 'jobs',
        defaultLoadingError: 'Sorry, there was an error loading the jobs.',
    })
}

const useWorkOrder = (options, alwaysRefresh = false) => {
    return Model({
        assumeLoading: true,
        stateKey: ['work-order', options],
        alwaysRefresh: alwaysRefresh,
        persist: true,
        fetchData: () => API.get(`/work-orders?${toQueryString(options)}`),
        registerAdditionalMethods: (opts) => {
            return {
                optimise: (jobIds) => {
                    const workOrderId = opts.model()?.id ?? null;
                    return opts.wrapUpdateCall(
                        () => API.post(`/work-orders/optimise`, {workOrderId, jobIds, ...options}),
                        'Job order optimised',
                        true,
                    );
                },
                reorder: (jobIds) => {
                    const workOrderId = opts.model()?.id ?? null;
                    return opts.wrapUpdateCall(
                        () => API.post(`/work-orders`, {workOrderId, jobIds, ...options}),
                        'Job order saved',
                        true,
                    );
                },
                deleteWorkOrder: () => {
                    const workOrderId = opts.model()?.id ?? null;
                    return opts.wrapUpdateCall(
                        () => API.delete(`/work-orders/${workOrderId}`),
                        'Job order cleared',
                        true,
                    );
                },
            }
        },
        returnDataKey: 'order',
        defaultLoadingError: 'Sorry, there was an error loading the work order.',
    })
}

const useWorkOrderSimilar = (jobIds, block = false, alwaysRefresh = false) => {
    return Model({
        block: block || !jobIds || (jobIds?.length < 3),
        stateKey: ['work-order-similar', jobIds],
        alwaysRefresh: alwaysRefresh,
        persist: true,
        fetchData: () => API.get(`/work-orders/similar?${toQueryString({jobIds})}`),
        returnDataKey: 'workOrder',
        defaultLoadingError: 'Sorry, there was an error loading the work orders.',
    });
}

const useScheduleMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/jobs/schedule', data),
        'Jobs were scheduled',
        'Sorry, there was an error scheduling the jobs.',
    )
}

const useScheduleWeek = () => {
    return useSimpleWrapper(
        (data) => API.post('/jobs/schedule/week', data),
        'Jobs were scheduled',
        'Sorry, there was an error scheduling the jobs.',
    )
}

const useDoneMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/jobs/done', data),
        'Jobs were marked as done',
        'Sorry, there was an error marking the jobs as done',
    )
}

const useSkipMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/jobs/skip', data),
        'Jobs were skipped',
        'Sorry, there was an error skipping the jobs',
    )
}

const useAddToRoundMany = () => {
    return useSimpleWrapper(
        (data) => API.post('/jobs/addToRound', data),
        'Jobs were added to round',
        'Sorry, there was an error adding the jobs to the round',
    )
}

const statuses = {
    'Late': {
        shortName: 'Late',
        class: 'danger',
        colors: 'stroke-red-600 fill-red-600',
        bgColor: 'bg-red-600 ring-red-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-red-600',
        icon: 'TriangleExclamation',
        shortDescription: 'Past the scheduled date but not marked as done',
        considerDone: false,
        considerScheduled: true,
        excludeFromTotals: false,
    },
    'Today': {
        shortName: 'Today',
        class: 'success',
        colors: 'stroke-green-500 fill-green-500',
        bgColor: 'bg-green-500 ring-green-500',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-green-500',
        icon: 'Play',
        shortDescription: 'Scheduled for today',
        considerDone: false,
        considerScheduled: true,
        excludeFromTotals: false,
    },
    'Tomorrow': {
        shortName: 'Tomorrow',
        class: 'success',
        colors: 'stroke-green-800 fill-green-800',
        bgColor: 'bg-green-800 ring-green-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-green-800',
        icon: 'ForwardStep',
        shortDescription: 'Scheduled for tomorrow',
        considerDone: false,
        considerScheduled: true,
        excludeFromTotals: false,
    },
    'Overdue': {
        shortName: 'Overdue',
        class: 'warning',
        colors: 'stroke-orange-600 fill-orange-600',
        bgColor: 'bg-orange-600 ring-orange-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-orange-600',
        icon: 'Triangle',
        shortDescription: 'Past the due week but not scheduled',
        considerDone: false,
        considerScheduled: false,
        excludeFromTotals: false,
    },
    'Scheduled': {
        shortName: 'Scheduled',
        class: 'primary',
        colors: 'stroke-indigo-600 fill-indigo-600',
        bgColor: 'bg-indigo-600 ring-indigo-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-indigo-600',
        icon: 'CalendarCheck',
        shortDescription: 'Scheduled for a date in the future',
        considerDone: false,
        considerScheduled: true,
        excludeFromTotals: false,
    },
    'Upcoming': {
        shortName: 'Upcoming',
        class: 'info',
        colors: 'stroke-blue-600 fill-blue-600',
        bgColor: 'bg-blue-600 ring-blue-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-blue-600',
        icon: 'CalendarClock',
        shortDescription: 'Due next week',
        considerDone: false,
        considerScheduled: false,
        excludeFromTotals: false,
    },
    'Due': {
        shortName: 'Due',
        class: 'primary',
        colors: 'stroke-yellow-600 fill-yellow-600',
        bgColor: 'bg-yellow-600 ring-yellow-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-yellow-600',
        icon: 'CalendarPlus',
        shortDescription: 'Due this week but not scheduled',
        considerDone: false,
        considerScheduled: false,
        excludeFromTotals: false,
    },
    'Idle': {
        shortName: 'Idle',
        class: 'secondary',
        colors: 'stroke-gray-500 fill-gray-500',
        bgColor: 'bg-gray-500 ring-gray-500',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-gray-500',
        icon: 'Circle',
        shortDescription: 'Not a recurring job',
        considerDone: true,
        considerScheduled: false,
        excludeFromTotals: false,
    },
    'Done': {
        shortName: 'Done',
        class: 'secondary',
        colors: 'stroke-teal-700 fill-teal-700',
        bgColor: 'bg-teal-700 ring-teal-700',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-teal-700',
        icon: 'Check',
        shortDescription: 'Previously done and not yet due again',
        considerDone: true,
        considerScheduled: false,
        excludeFromTotals: false,
    },
    'Cancelled': {
        shortName: 'Cancelled',
        class: 'secondary',
        colors: 'stroke-pink-800 fill-pink-800',
        bgColor: 'bg-pink-800 ring-pink-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-pink-800',
        icon: 'XMark',
        shortDescription: 'No longer a recurring job',
        considerDone: true,
        considerScheduled: false,
        excludeFromTotals: true,
    },
    'Skipped': {
        shortName: 'Skipped',
        class: 'secondary',
        colors: 'stroke-purple-600 fill-purple-600',
        bgColor: 'bg-purple-600 ring-purple-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-purple-600',
        icon: 'CalendarX',
        shortDescription: 'The job has been skipped',
        considerDone: true,
        considerScheduled: false,
        excludeFromTotals: true,
    },
    'Customer Skipped': {
        shortName: 'C.Skipped',
        class: 'secondary',
        colors: 'stroke-fuchsia-600 fill-fuchsia-600',
        bgColor: 'bg-fuchsia-600 ring-fuchsia-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-fuchsia-600',
        icon: 'CalendarX',
        shortDescription: 'The job has been skipped by the customer',
        considerDone: true,
        considerScheduled: false,
        excludeFromTotals: true,
    },
    'Forecast': {
        shortName: 'Forecast',
        class: 'secondary',
        colors: 'stroke-gray-600 fill-gray-600',
        bgColor: 'bg-gray-600 ring-gray-600',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'stroke-gray-600',
        icon: 'CrystalBall',
        shortDescription: 'The job is forecasted for the future',
        considerDone: false,
        considerScheduled: false,
        excludeFromTotals: true,
    },
};

const doneStatuses = Object.values(statuses).filter(status => status.considerDone).map(status => status.shortName);
const scheduledStatuses = Object.values(statuses).filter(status => status.considerScheduled).map(status => status.shortName);

export {
    useJobs,
    useJob,
    useJobSummary,
    useScheduleSuggestion,
    useWorkDate,
    useWorkOrder,
    useWorkOrderSimilar,
    useScheduleMany,
    useDoneMany,
    useSkipMany,
    useScheduleWeek,
    useAddToRoundMany,
    statuses,
    doneStatuses,
    scheduledStatuses,
}
